import { Suspense, useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  RouteObject,
  Outlet,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";

import { setupAuthInterceptors } from "@/lib/services/auth-refresh";
import { AppConfig } from "@/config";
import { APPS } from "@/lib/constants";
import { getAuthorizedRoutes, PermissionChecker } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/toaster";
import { Spinner } from "@/components/ui/spinner";
import { AuthCallback } from "@/components/auth-callback";
import NotFound from "@/components/not-found";
import UnauthorizedPage from "@/components/unauthorized-page";
import LoginPage from "@/app/auth/login/page";
import HomePage from "./page";
import AppsLayout from "./apps/layout";

import { AppProvider } from "./app-context";
import { AuthWrapper, ProtectedRoute } from "./auth-context";
import { useAuthStore } from "@/store/auth-store";

// Setup
setupAuthInterceptors();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: AppConfig.env === "production",
      staleTime: 0, //always fresh
    },
  },
});

// Route builder
const buildAppRoutes = (checker: PermissionChecker): RouteObject[] => {
  const routes: RouteObject[] = [];


  Object.values(APPS).forEach((app) => {
    const authorizedRoutes = getAuthorizedRoutes(app, checker);

    authorizedRoutes.forEach((route) => {
      routes.push({
        path: route.path,
        element: (
          <ProtectedRoute
            element={
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-full">
                    <Spinner>Loading...</Spinner>
                  </div>
                }
              >
                <route.component />
              </Suspense>
            }
            requiredEntityType={route.permission?.entityType}
            requiredAction={route.permission?.action}
            requiredGroup={route.permission?.group}
          />
        ),
      });
    });
  });

  return routes;
};

// Router configuration
const createRouter = (isAuthenticated: boolean, checker: PermissionChecker) => {
  return createBrowserRouter([
    {
      path: "/",
      element: (
        <ErrorBoundary
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          fallbackRender={({ error, resetErrorBoundary }: FallbackProps) => (
            <div className="flex h-[calc(100dvh-4rem)] flex-col items-center justify-center gap-2">
              <h2 className="text-2xl font-bold">Something went wrong!</h2>
              <Button onClick={resetErrorBoundary} variant="outline">
                Try again
              </Button>
            </div>
          )}
          onReset={() => window.location.reload()}
        >
          <AuthWrapper>
            <AppProvider>
              <Outlet />
            </AppProvider>
          </AuthWrapper>
        </ErrorBoundary>
      ),
      children: [
        {
          index: true,
          element: <ProtectedRoute element={<HomePage />} />,
        },
        {
          element: <AppsLayout />,
          children: isAuthenticated ? buildAppRoutes(checker) : [],
        },
        {
          path: "login",
          element: <LoginPage />,
        },
        {
          path: "auth/callback",
          element: <AuthCallback />,
        },
        {
          path: "unauthorized",
          element: <UnauthorizedPage />,
        },
        {
          path: "*",
          element: <NotFound />,
        },
      ],
    },
  ]);
};

// Main App component
export const App = () => {
  const { isAuthenticated, hasEntityAction, isInGroup } = useAuthStore();

  const router = useMemo(
    () => createRouter(isAuthenticated, { hasEntityAction, isInGroup }),
    [isAuthenticated, hasEntityAction, isInGroup]
  );

  return (
    <GoogleOAuthProvider clientId={AppConfig.googleClientId}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster />
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
};
